'use client';

import React, { useEffect, useState } from 'react';
import QuantitySelector from '@/src/app/components/QuantitySelector/QuantitySelector.tsx';
import classNames from 'classnames';
import AddToCartButton from '@/src/app/components/AddToCartButton/AddToCart.tsx';
import { Dictionary } from '@/src/lib/dictionary/types.ts';
import { DeserializedCart } from '@/src/lib/cart/glue/types/deserialized.ts';
import { DeserializedConcreteProduct, ExtendedProductListItem } from '@/src/lib/catalog-search/types.ts';
import CalculatePriceButton from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/components/CalculatePriceButton/CalculatePriceButton.client.tsx';
import { isProductBuyable } from '@/src/lib/product/business/product-stock.ts';
import { CalculatePriceRequest } from '@/src/lib/product/models/calculate-price-request.model.ts';
import { DeserializedCompanyUser } from '@/src/lib/company-user/types.ts';
import { handleException } from '@/src/lib/core/exception/handle-exception.ts';
import { NotificationType } from '@/src/app/components/Notification/types.ts';
import { useNotify } from '@/src/lib/notification/notify.ts';
import { useAddToCart } from '@/src/lib/cart/hooks/use-add-to-cart.ts';
import { eventBus } from '@/src/lib/core/event/bus.ts';
import { EventBusName } from '@/src/lib/core/event/types.ts';
import { failure } from '@/src/lib/http/business/server-action-results.ts';
import { addToCartAction } from '@/src/lib/cart/business/add-to-cart.action.ts';
import { CalculatePriceResponse } from '@/src/lib/product/types.ts';
import { isUserVerified } from '@/src/lib/auth/business/user-status.ts';
import { GetUser } from '@/src/lib/auth/types.ts';
import { calculatePriceClient } from '@/src/lib/product/business/calculate-price.client.ts';
export type AddToCartWithQuantityProps = {
  lang: string;
  country: string;
  locale: string;
  dict: Dictionary;
  cart?: DeserializedCart;
  user: GetUser;
  className?: string;
  product: ExtendedProductListItem;
  concreteProduct: DeserializedConcreteProduct;
  isCallStockQuantityOnPLPFeatureEnabled?: boolean;
  accountNumber?: string;
  pricingReference?: string;
  queryYourPrice: boolean;
  companyUsers: Array<DeserializedCompanyUser>;
  initialCalculatePrice: CalculatePriceResponse;
  addToCart: typeof addToCartAction;
  currency: string;
  searchParams?: {
    [key: string]: string | Array<string> | undefined;
  };
};
function AddToCartWithQuantityClient({
  dict,
  lang,
  country,
  locale,
  cart,
  user,
  className,
  product,
  concreteProduct,
  isCallStockQuantityOnPLPFeatureEnabled,
  accountNumber,
  queryYourPrice,
  pricingReference,
  initialCalculatePrice,
  currency,
  searchParams,
  addToCart
}: AddToCartWithQuantityProps) {
  const {
    minimumOrderAmountFromProduct,
    initialQuantity,
    quantity,
    setQuantity,
    onAddToCartClick,
    calculatedPrice,
    setCalculatedPrice,
    selectedPricingReference,
    isAddToCartPending,
    success,
    error
  } = useAddToCart(concreteProduct, initialCalculatePrice, addToCart, dict, lang, cart);
  const [yourPriceError, setYourPriceError] = useState<null | Error>(null);
  const [isYourPriceLoading, setIsYourPriceLoading] = useState(false);
  const {
    flashNotification
  } = useNotify();
  useEffect(() => {
    function onAccountOrPricingReferenceChange() {
      setCalculatedPrice(initialCalculatePrice);
      setQuantity(1);
    }
    eventBus.on(EventBusName.ACCOUNT_OR_PRICING_REFERENCE_CHANGED, onAccountOrPricingReferenceChange);
    return () => {
      eventBus.off(EventBusName.ACCOUNT_OR_PRICING_REFERENCE_CHANGED, onAccountOrPricingReferenceChange);
    };
  }, []);
  useEffect(() => {
    setCalculatedPrice(initialCalculatePrice);
    setQuantity(initialQuantity);
  }, [searchParams?.sort, searchParams?.page, searchParams?.q]);
  async function onQuantityChange(quantity_0: number, queryStockQuantity: boolean = true) {
    try {
      setQuantity(quantity_0);
      setIsYourPriceLoading(true);
      setYourPriceError(null);
      const calculatePriceRequest = new CalculatePriceRequest(concreteProduct, quantity_0, locale, isUserVerified(user), isCallStockQuantityOnPLPFeatureEnabled === true && queryStockQuantity);
      if (queryYourPrice && accountNumber) {
        calculatePriceRequest.setQueryYourPrice(accountNumber, selectedPricingReference);
      }
      const response = await calculatePriceClient(calculatePriceRequest.resultForClient);

      // eslint-disable-next-line no-console
      console.debug('Calculate Price:', response);
      // eslint-disable-next-line no-console
      console.debug('isBuyable:', isProductBuyable(concreteProduct));
      // eslint-disable-next-line no-console
      console.debug('minimumOrderAmountFromProduct:', minimumOrderAmountFromProduct);
      setQuantity(response.quantity);
      setCalculatedPrice(response);
      if (response.messages?.length) {
        // eslint-disable-next-line no-console
        console.info(`Calculate Price Messages: %c${response.messages.join(' ')} %c`, 'font-weight: bold;color:#fc0', '');
        if (response.messages.some(message => message.includes(dict.product.yourPriceError))) {
          flashNotification(dict.product.yourPriceError, NotificationType.ERROR);
          return failure('Failed to calculate your price');
        }
      }
    } catch (e) {
      handleException(e as Error);
    } finally {
      setIsYourPriceLoading(false);
    }
  }
  async function onAddToCart() {
    let fetchYourPriceResponse;
    if (!calculatedPrice.yourPrice && isUserVerified(user)) {
      fetchYourPriceResponse = await onQuantityChange(quantity);
    }
    if (fetchYourPriceResponse) {
      return;
    }
    onAddToCartClick();
  }
  const errorFromCalculatePrice = calculatedPrice.messages.length > 0 ? new Error(calculatedPrice.messages.join(' ')) : null;
  return <>
      <CalculatePriceButton className="bg-white w-full lg:w-auto h-[50px] py-3 px-4 border-gray-200 md:border-l inline-flex items-center" dict={dict} product={product} user={user} lang={lang} country={country} pricingReference={pricingReference} accountNumber={accountNumber} isLoading={isYourPriceLoading} isError={yourPriceError !== null || calculatedPrice.messages.length > 0} error={yourPriceError || errorFromCalculatePrice} onCalculatePriceClick={() => onQuantityChange(quantity, false)} yourPriceData={calculatedPrice.yourPrice ? {
      ...calculatedPrice.yourPrice,
      currencyIsoCode: calculatedPrice.currencyIsoCode
    } : null} currencyIsoCode={currency} />
      <div className={classNames(className, 'inline-flex flex-row items-center justify-between')}>
        <QuantitySelector quantity={quantity} onQuantityChange={onQuantityChange} className="border-y-0" disabled={isYourPriceLoading} withDebounce={true} minVal={initialQuantity} />
        <div className={classNames('flex items-center h-full')}>
          <AddToCartButton error={error} success={success} isAddToCartPending={isAddToCartPending} onButtonClick={onAddToCart} dict={dict} disabled={!calculatedPrice?.canBeAddedToCart || isYourPriceLoading} />
        </div>
      </div>
    </>;
}
export default AddToCartWithQuantityClient;